/* eslint-disable react/jsx-no-constructed-context-values */
/* eslint-disable react-hooks/exhaustive-deps */

import type { FC, ReactNode } from 'react'
import { createContext, useState, useContext, useMemo } from 'react'

import { getDefaultCountryId } from '@skytech/utils'

import { useAppContext } from '@/app/index.context'
import { useSearchParamsByKey } from '@/hooks/searchParams'
import type { Address, Product, SortOrder } from '@/modules/ecom-policy/types'

import type { NewOrderStepName } from './types'

type ShopNewOrderContext = {
  costObjectId: [number | null, (v: number | null) => void]
  selectedPolicyId: [number | null, (v: number | null) => void]
  selectedProductIds: [number[] | null, (v: number[] | null) => void]
  mandatoryProducts: [Product[] | null, (v: Product[] | null) => void]
  transportationProduct: [Product | null, (v: Product | null) => void]
  productQuantityById: [Record<number, number>, (v: Record<number, number>) => void]
  selectedCategoryIds: [number[] | null, (v: number[] | null) => void]
  selectedAddressId: [number | null, (v: number | null) => void]
  customAddress: [Partial<Address> | null, (v: Partial<Address> | null) => void]
  searchFilter: string
  setSearchFilter: (v: string) => void
  comment: [string, (v: string) => void]
  stockFilter: [boolean, (v: boolean) => void]
  sortParam: ['priceFrom' | undefined, (v: 'priceFrom' | undefined) => void]
  sortOrder: [SortOrder, (v: SortOrder) => void]
  activeStepName: [NewOrderStepName, (v: NewOrderStepName) => void]
  contactName: [string | null, (v: string | null) => void]
  contactNumber: [
    { phoneCountryCode: number | null; phoneNumber: string },
    (v: { phoneCountryCode: number | null; phoneNumber: string }) => void,
  ]
  isWholeVendorsCatalogueVisible: [boolean, (v: boolean) => void]
  resetOrderData: (keepDefaultAddress?: boolean) => void
  isCostObjectValid: [boolean, (v: boolean) => void]
  isPrivateDeliveryAddress: [boolean, (v: boolean) => void]
  orderUserType: [
    'employee' | 'location' | 'user' | null,
    (v: 'employee' | 'location' | 'user' | null) => void,
  ]
}

const Context = createContext<ShopNewOrderContext>({
  costObjectId: [null, () => {}],
  selectedPolicyId: [null, () => {}],
  selectedProductIds: [null, () => {}],
  productQuantityById: [{}, () => {}],
  selectedCategoryIds: [null, () => {}],
  selectedAddressId: [null, () => {}],
  customAddress: [null, () => {}],
  searchFilter: '',
  setSearchFilter: () => {},
  comment: ['', () => {}],
  stockFilter: [false, () => {}],
  sortParam: [undefined, () => {}],
  sortOrder: ['asc', () => {}],
  activeStepName: ['USER', () => {}],
  isWholeVendorsCatalogueVisible: [false, () => {}],
  mandatoryProducts: [null, () => {}],
  transportationProduct: [null, () => {}],
  contactName: [null, () => {}],
  contactNumber: [{ phoneCountryCode: null, phoneNumber: '' }, () => {}],
  resetOrderData: () => {},
  isCostObjectValid: [false, () => {}],
  isPrivateDeliveryAddress: [false, () => {}],
  orderUserType: [null, () => {}],
})

export const ShopNewOrderContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [querySearch] = useSearchParamsByKey('search')
  const [searchFilter, setSearchFilter] = useState(querySearch || '')

  const costObjectId = useState<null | number>(null)
  const selectedPolicyId = useState<number | null>(null)
  const selectedProductIds = useState<number[] | null>(null)
  const productQuantityById = useState<Record<number, number>>({})
  const selectedCategoryIds = useState<number[] | null>(null)
  const selectedAddressId = useState<number | null>(null)
  const customAddress = useState<Partial<Address> | null>(null)
  const comment = useState<string>('')
  const stockFilter = useState<boolean>(false)
  const sortParam = useState<'priceFrom' | undefined>()
  const sortOrder = useState<SortOrder>('asc')
  const activeStepName = useState<NewOrderStepName>('USER')
  const mandatoryProducts = useState<Product[] | null>(null)
  const isWholeVendorsCatalogueVisible = useState(false)
  const transportationProduct = useState<Product | null>(null)
  const contactName = useState<string | null>(null)
  const { locale } = useAppContext()
  const defaultPhoneCountryId = getDefaultCountryId(locale)
  const isCostObjectValid = useState(false)
  const isPrivateDeliveryAddress = useState<boolean>(false)
  const orderUserType = useState<'employee' | 'location' | 'user' | null>(null)

  const contactNumber = useState<{ phoneCountryCode: number | null; phoneNumber: string }>({
    phoneCountryCode: defaultPhoneCountryId,
    phoneNumber: '',
  })

  function resetOrderData(keepDefaultAddress?: boolean) {
    selectedCategoryIds[1](null)
    selectedProductIds[1](null)
    transportationProduct[1](null)
    mandatoryProducts[1](null)
    selectedPolicyId[1](null)
    isPrivateDeliveryAddress[1](false)
    if (!keepDefaultAddress) selectedAddressId[1](null)
  }

  const value: ShopNewOrderContext = useMemo(
    () => ({
      costObjectId,
      selectedPolicyId,
      selectedProductIds,
      selectedAddressId,
      customAddress,
      searchFilter,
      setSearchFilter,
      comment,
      selectedCategoryIds,
      stockFilter,
      sortParam,
      sortOrder,
      activeStepName,
      mandatoryProducts,
      transportationProduct,
      productQuantityById,
      contactName,
      contactNumber,
      isWholeVendorsCatalogueVisible,
      resetOrderData,
      isCostObjectValid,
      isPrivateDeliveryAddress,
      orderUserType,
    }),
    [
      activeStepName[0],
      comment[0],
      costObjectId[0],
      customAddress[0],
      JSON.stringify(mandatoryProducts[0]),
      transportationProduct[0]?.id,
      searchFilter,
      selectedAddressId[0],
      JSON.stringify(selectedCategoryIds[0]),
      selectedPolicyId[0],
      JSON.stringify(selectedProductIds[0]),
      sortOrder[0],
      sortParam[0],
      stockFilter[0],
      JSON.stringify(productQuantityById[0]),
      isWholeVendorsCatalogueVisible[0],
      contactName[0],
      contactNumber[0],
      isCostObjectValid,
      isPrivateDeliveryAddress,
      orderUserType,
    ]
  )

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export const useShopNewOrderContext = () => useContext(Context)
